import { useContext, useEffect, useState } from "react";
import { AuthEventType, AuthInteractionStatus } from "../../auth/cognito-browser";
import { useCognito, useIsAuthenticated } from "../../auth/cognito-react";
import { getLoginRequest, getUnifiedRequest } from "../../config/runtime-auth";
import { resolveTenantCode } from "../../config/tenant-code";
import { getBatchConfig } from "../../../data/configAPI";
import { ThemeContext } from "../../../contexts/ThemeContext";
import { ConfiguratorContext } from "../../../contexts/ConfiguratorContext";
import {
  CONFIGURATOR_KEYS,
  THEME_BACKGROUND_COLOR_KEY,
  THEME_ERROR_COLOR_KEY,
  THEME_INFO_COLOR_KEY,
  THEME_PAPER_COLOR_KEY,
  THEME_PRIMARY_COLOR_KEY,
  THEME_SECONDARY_COLOR_KEY,
  THEME_SUCCESS_COLOR_KEY,
  THEME_WARNING_COLOR_KEY,
} from "../../constants/Configurator.constants";
import MissingTenantNotice from "../MissingTenantNotice";
import InternetStopNotice from "./InternetStopNotice";
import { FlexContainer } from "../styled/Container.styled";
import { acquireToken } from "../../utils/auth-functions";
import {
  extractLoanNicknameFromDetails,
  hasValidLoanNickname,
  isBatchResponseOk,
  mergeLoanNicknameInList,
} from "../../utils/utility-functions";
import { AuthContext } from "../../../contexts/AuthContext";
import { getLoanData } from "../../../data/loanAPI";
import {
  CLIENT_ADMIN_ROLE,
  PAYMENTS_SUPPORT_ROLE,
} from "../../constants/Roles.constants";
import { useNavigate } from "react-router-dom";
import { withAugmentedPalette } from "../../utils/brandingTheme";
import { saveBrandingColorsToSession } from "../../utils/brandingSession";

const RequireAuth = (props) => {
  const navigate = useNavigate();
  const { instance, accounts, inProgress } = useCognito();
  const { addPendingRequest, removePendingRequest } = useContext(ThemeContext);
  const {
    edittedFields,
    addBatchOfEdittedFields,
    hasRetrievedConfig,
    setHasRetrievedConfig,
  } = useContext(ConfiguratorContext);
  const {
    token,
    setToken,
    userDetails,
    setUserDetails,
    userRoles,
    setuserRoles,
    setVisibleRole,
    impersonatedUser,
    loans,
    setLoans,
    activeLoan,
    setActiveLoan,
    activeLoanDetails,
    setActiveLoanDetails,
  } = useContext(AuthContext);
  const isAuthenticated = useIsAuthenticated();
  const { theme, setTheme } = useContext(ThemeContext);

  const [missingTenant, setMissingTenant] = useState(false);

  useEffect(() => {
    resolveTenantCode();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      if (inProgress === AuthInteractionStatus.None && !token) {
        if (!userDetails) {
          setUserDetails(accounts[0]);
          setuserRoles(accounts[0].idTokenClaims.roles);
          setVisibleRole(
            accounts[0].idTokenClaims.roles.includes(CLIENT_ADMIN_ROLE)
              ? CLIENT_ADMIN_ROLE
              : accounts[0].idTokenClaims.roles[0],
          );
        }
        const request = {
          scopes: getUnifiedRequest().scopes,
          extraQueryParameters: getLoginRequest().extraQueryParameters,
          account: accounts[0],
        };
        acquireToken(
          instance,
          request,
          { checkLoans: false },
          addPendingRequest,
          removePendingRequest,
          setMissingTenant,
          userRoles,
          impersonatedUser,
          true,
          setToken,
        );
      }
    }
    if (!isAuthenticated) {
      if (inProgress === AuthInteractionStatus.None) {
        const hasAuthCallbackParams =
          window.location.search.includes("code=") &&
          window.location.search.includes("state=");
        if (hasAuthCallbackParams || window.location.pathname === "/logout") {
          return;
        }
        addPendingRequest();
        if (sessionStorage.getItem("co")) {
          setMissingTenant(false);
          instance
            .loginRedirect(getLoginRequest())
            .then((data) => {
              console.log(data);
            })
            .catch((e) => {
              console.error(e);
            });
        } else {
          setMissingTenant(true);
          removePendingRequest();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, inProgress, isAuthenticated]);

  useEffect(() => {
    if (!activeLoan && token) {
      const request = {
        scopes: getUnifiedRequest().scopes,
        extraQueryParameters: getLoginRequest().extraQueryParameters,
        account: accounts[0],
      };
      const loanInfo = {
        checkLoans: true,
        loans: loans,
        setLoans: setLoans,
        setActiveLoan: setActiveLoan,
      };
      acquireToken(
        instance,
        request,
        loanInfo,
        addPendingRequest,
        removePendingRequest,
        setMissingTenant,
        userRoles,
        impersonatedUser,
      );
    }
    if (edittedFields.length === 0 && token) {
      callConfigApi(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const getLoanDetails = async () => {
      if (
        token &&
        activeLoan &&
        (!activeLoanDetails ||
          (activeLoan !== "no-loan" &&
            activeLoan !== activeLoanDetails?.loanDetails?.loanId))
      ) {
        addPendingRequest();

        const loanDetails = await getLoanData(
          token,
          activeLoan,
          impersonatedUser,
        );
        const loanId = loanDetails?.loanDetails?.loanId;
        if (loanId === activeLoan) {
          setActiveLoanDetails(loanDetails);

          const nickname = extractLoanNicknameFromDetails(loanDetails);
          if (hasValidLoanNickname(nickname)) {
            setLoans((prev) =>
              mergeLoanNicknameInList(
                Array.isArray(prev) ? prev : [],
                loanId,
                nickname,
              ),
            );
          }
        }

        removePendingRequest();
      }
    };

    getLoanDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, activeLoan]);

  useEffect(() => {
    const callbackId = instance.addEventCallback((message) => {
      if (
        message.eventType === AuthEventType.LOGIN_SUCCESS ||
        message.eventType === AuthEventType.ACQUIRE_TOKEN_SUCCESS
      ) {
        if (accounts[0]?.idTokenClaims?.roles?.includes(CLIENT_ADMIN_ROLE)) {
          navigate("/client-configurations");
          instance.removeEventCallback(callbackId);
        } else if (
          accounts[0]?.idTokenClaims?.roles?.includes(PAYMENTS_SUPPORT_ROLE)
        ) {
          navigate("/support");
          instance.removeEventCallback(callbackId);
        }
      }
    });

    return () => {
      if (callbackId) {
    
        instance.removeEventCallback(callbackId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts]);

  const callConfigApi = async (token) => {
    addPendingRequest();
    const body = CONFIGURATOR_KEYS.map((item) => {
      return {
        operation: "GET",
        property: {
          key: item,
        },
      };
    });
    const configData = await getBatchConfig(token, body, impersonatedUser);

    // Build one deep-cloned theme with all palette properties copied before
    // applying DB values — avoids repeated setTheme calls and direct state mutation.
    const newTheme = {
      ...theme,
      palette: {
        ...theme.palette,
        primary: { ...theme.palette.primary },
        secondary: { ...theme.palette.secondary },
        info: { ...theme.palette.info },
        success: { ...theme.palette.success },
        warning: { ...theme.palette.warning },
        error: { ...theme.palette.error },
        background: { ...theme.palette.background },
      },
    };

    const savedData = configData
      .filter((item) => isBatchResponseOk(item.httpStatus))
      .map((item) => {
        switch (item.property.key) {
          case THEME_PRIMARY_COLOR_KEY:
            newTheme.palette.primary.main = item.property.value;
            break;
          case THEME_SECONDARY_COLOR_KEY:
            newTheme.palette.secondary.main = item.property.value;
            break;
          case THEME_INFO_COLOR_KEY:
            newTheme.palette.info.main = item.property.value;
            break;
          case THEME_SUCCESS_COLOR_KEY:
            newTheme.palette.success.main = item.property.value;
            break;
          case THEME_WARNING_COLOR_KEY:
            newTheme.palette.warning.main = item.property.value;
            break;
          case THEME_ERROR_COLOR_KEY:
            newTheme.palette.error.main = item.property.value;
            break;
          case THEME_BACKGROUND_COLOR_KEY:
            newTheme.palette.background.default = item.property.value;
            break;
          case THEME_PAPER_COLOR_KEY:
            newTheme.palette.background.paper = item.property.value;
            break;
          default:
            break;
        }
        return {
          key: item.property.key,
          value: item.property.value,
          modified: false,
        };
      });

    const augmentedTheme = withAugmentedPalette(newTheme);
    setTheme(augmentedTheme);
    saveBrandingColorsToSession(augmentedTheme.palette);
    addBatchOfEdittedFields(savedData);
    setHasRetrievedConfig(true);
    removePendingRequest();
  };

  // US-1062468: an Internet Stop Flag of "1" blocks online access to the loan.
  // Show the access notice in place of the page content; the global navbar stays
  // mounted (in App.routing) so a borrower with other loans can switch to one
  // that is not stopped.
  const isInternetStopped =
    activeLoanDetails?.loanDetails?.internetStopFlag === "1";

  return isAuthenticated && hasRetrievedConfig ? (
    isInternetStopped ? (
      <InternetStopNotice />
    ) : (
      props.children
    )
  ) : missingTenant ? (
    <FlexContainer
      width="100%"
      minHeight="100vh"
      alignitems="center"
      justifycontent="center"
      padding="16px"
    >
      <MissingTenantNotice />
    </FlexContainer>
  ) : (
    ""
  );
};

export default RequireAuth;